import React, { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import {
  Framework, IndicatorConfig, PremiumModuleConfig,
  IndicatorCatalog, IndicatorDefinition, ThresholdBucket,
} from '../../../types/frameworks';
import {
  ArrowLeftIcon, PlusIcon, TrashIcon,
  ChevronDownIcon, ChevronUpIcon,
} from '@heroicons/react/24/outline';

interface FrameworkBuilderProps {
  framework: Framework | null;
  onClose: () => void;
}

const QUALITY_POINTS = 50;
const TIMING_POINTS = 50;

const FrameworkBuilder: React.FC<FrameworkBuilderProps> = ({ framework, onClose }) => {
  const [name, setName] = useState(framework?.name || '');
  const [description, setDescription] = useState(framework?.description || '');
  const [indicators, setIndicators] = useState<IndicatorConfig[]>(framework?.indicators || []);
  const [premiumModules, setPremiumModules] = useState<PremiumModuleConfig[]>(framework?.premium_modules || []);
  const [catalog, setCatalog] = useState<IndicatorCatalog | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.getIndicatorCatalog().then(setCatalog).catch(console.error);
  }, []);

  const qualityUsed = indicators.filter(i => i.dimension === 'quality').reduce((s, i) => s + i.weight, 0);
  const timingIndicatorUsed = indicators.filter(i => i.dimension === 'timing').reduce((s, i) => s + i.weight, 0);
  const timingModuleUsed = premiumModules.filter(pm => pm.enabled).reduce((s, pm) => s + pm.points_allocated, 0);
  const timingUsed = timingIndicatorUsed + timingModuleUsed;
  const qualityRemaining = QUALITY_POINTS - qualityUsed;
  const timingRemaining = TIMING_POINTS - timingUsed;

  const qualityIndicators = indicators.filter(i => i.dimension === 'quality');
  const timingIndicators = indicators.filter(i => i.dimension === 'timing');

  const addedKeys = new Set(indicators.map(i => i.indicator_key));
  const availableQuality = catalog?.quality_indicators.filter(i => !addedKeys.has(i.key)) || [];
  const availableTiming = catalog?.timing_indicators.filter(i => !addedKeys.has(i.key)) || [];

  const addIndicator = (def: IndicatorDefinition, dimension: 'quality' | 'timing') => {
    const remaining = dimension === 'quality' ? qualityRemaining : timingRemaining;
    const defaultWeight = Math.max(1, Math.min(remaining, 10));
    setIndicators(prev => [...prev, {
      indicator_key: def.key, dimension, weight: defaultWeight,
      thresholds: def.default_thresholds, sort_order: prev.length,
    }]);
  };

  const removeIndicator = (key: string) => setIndicators(prev => prev.filter(i => i.indicator_key !== key));

  const updateIndicatorWeight = (key: string, weight: number) => {
    setIndicators(prev => prev.map(i => i.indicator_key === key ? { ...i, weight: Math.max(1, weight) } : i));
  };

  const updateIndicatorThresholds = (key: string, thresholds: ThresholdBucket[]) => {
    setIndicators(prev => prev.map(i => i.indicator_key === key ? { ...i, thresholds } : i));
  };

  const togglePremiumModule = (moduleKey: string) => {
    setPremiumModules(prev => {
      const existing = prev.find(pm => pm.module_key === moduleKey);
      if (existing) return prev.map(pm => pm.module_key === moduleKey ? { ...pm, enabled: !pm.enabled } : pm);
      const def = catalog?.premium_modules.find(m => m.key === moduleKey);
      return [...prev, { module_key: moduleKey, points_allocated: def?.default_points || 15, enabled: true }];
    });
  };

  const updateModulePoints = (moduleKey: string, points: number) => {
    setPremiumModules(prev => prev.map(pm => pm.module_key === moduleKey ? { ...pm, points_allocated: Math.max(1, points) } : pm));
  };

  const handleSave = async () => {
    setError(null);
    if (!name.trim()) { setError('Framework name is required'); return; }
    if (qualityRemaining !== 0) { setError(`Quality dimension has ${qualityRemaining} unallocated points`); return; }
    if (timingRemaining !== 0) { setError(`Timing dimension has ${timingRemaining} unallocated points`); return; }

    setSaving(true);
    try {
      const data = {
        name: name.trim(), framework_type: 'scoring' as const,
        description: description.trim() || undefined,
        quality_points: QUALITY_POINTS, timing_points: TIMING_POINTS,
        indicators, premium_modules: premiumModules,
      };
      if (framework) await apiService.updateFramework(framework.id, data);
      else await apiService.createFramework(data);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to save framework');
    } finally { setSaving(false); }
  };

  if (!catalog) {
    return <div className="st-muted-block">Loading catalog...</div>;
  }

  return (
    <div className="framework-builder">
      <div className="st-fb-head">
        <button className="fw-btn-icon" onClick={onClose}>
          <ArrowLeftIcon className="st-ico" />
        </button>
        <h2 className="st-fb-title">
          {framework ? 'Edit Framework' : 'New Framework'}
        </h2>
      </div>

      <div className="framework-builder__input-row">
        <input className="framework-builder__input st-flex-1" type="text" value={name}
          onChange={e => setName(e.target.value)} placeholder="Framework name" />
        <input className="framework-builder__input st-flex-2" type="text" value={description}
          onChange={e => setDescription(e.target.value)} placeholder="Description (optional)" />
      </div>

      {/* Two Dimension Panels */}
      <div className="dimension-panels-grid">
        <DimensionPanel title="Quality" subtitle="Is it a good CC stock?" color="var(--tk-t1)"
          totalPoints={QUALITY_POINTS} usedPoints={qualityUsed} indicators={qualityIndicators}
          availableIndicators={availableQuality} catalog={catalog}
          onAdd={def => addIndicator(def, 'quality')} onRemove={removeIndicator}
          onWeightChange={updateIndicatorWeight} onThresholdsChange={updateIndicatorThresholds} />

        <DimensionPanel title="Timing" subtitle="Is now the right moment?" color="var(--tk-t1)"
          totalPoints={TIMING_POINTS} usedPoints={timingUsed} indicators={timingIndicators}
          availableIndicators={availableTiming} catalog={catalog}
          onAdd={def => addIndicator(def, 'timing')} onRemove={removeIndicator}
          onWeightChange={updateIndicatorWeight} onThresholdsChange={updateIndicatorThresholds}
          premiumModules={premiumModules} availablePremiumModules={catalog.premium_modules}
          onToggleModule={togglePremiumModule} onModulePointsChange={updateModulePoints} />
      </div>

      {error && <div className="fw-error-banner">{error}</div>}

      <div className="st-fb-foot">
        <button className="fw-btn-secondary" onClick={onClose}>Cancel</button>
        <button className="fw-btn-primary" onClick={handleSave} disabled={saving}
          style={{ opacity: saving ? 0.6 : 1, cursor: saving ? 'wait' : 'pointer' }}>
          {saving ? 'Saving...' : framework ? 'Save Changes' : 'Create Framework'}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// DIMENSION PANEL
// =============================================================================

const DimensionPanel: React.FC<{
  title: string; subtitle: string; color: string;
  totalPoints: number; usedPoints: number;
  indicators: IndicatorConfig[]; availableIndicators: IndicatorDefinition[];
  catalog: IndicatorCatalog;
  onAdd: (def: IndicatorDefinition) => void;
  onRemove: (key: string) => void;
  onWeightChange: (key: string, weight: number) => void;
  onThresholdsChange: (key: string, thresholds: ThresholdBucket[]) => void;
  premiumModules?: PremiumModuleConfig[];
  availablePremiumModules?: { key: string; display_name: string; description: string; default_points: number }[];
  onToggleModule?: (key: string) => void;
  onModulePointsChange?: (key: string, points: number) => void;
}> = ({ title, subtitle, color, totalPoints, usedPoints, indicators, availableIndicators, catalog,
       onAdd, onRemove, onWeightChange, onThresholdsChange,
       premiumModules, availablePremiumModules, onToggleModule, onModulePointsChange }) => {
  const remaining = totalPoints - usedPoints;

  return (
    <div className="dimension-panel">
      <div className="st-dim-head">
        <div>
          <h3 className="st-dim-title" style={{ color }}>{title}</h3>
          <p className="st-dim-sub">{subtitle}</p>
        </div>
        <span className="st-dim-pts" style={{
          color: remaining === 0 ? 'var(--tk-gain)' : remaining < 0 ? 'var(--tk-loss)' : 'var(--tk-warn)' }}>
          {usedPoints}/{totalPoints} pts
        </span>
      </div>

      {indicators.map(ind => {
        const allInd = [...(catalog.quality_indicators || []), ...(catalog.timing_indicators || [])];
        const def = allInd.find(d => d.key === ind.indicator_key);
        return (
          <IndicatorRow key={ind.indicator_key} config={ind} definition={def}
            onWeightChange={w => onWeightChange(ind.indicator_key, w)}
            onRemove={() => onRemove(ind.indicator_key)}
            onThresholdsChange={t => onThresholdsChange(ind.indicator_key, t)} />
        );
      })}

      {premiumModules && availablePremiumModules && onToggleModule && onModulePointsChange && (
        <div style={{ marginTop: indicators.length > 0 ? '10px' : 0 }}>
          {availablePremiumModules.map(pmDef => {
            const config = premiumModules.find(pm => pm.module_key === pmDef.key);
            const isEnabled = config?.enabled ?? false;
            return (
              <div key={pmDef.key} className={`premium-module-row ${isEnabled ? 'premium-module-row--enabled' : ''}`}>
                <input type="checkbox" className="st-check" checked={isEnabled} onChange={() => onToggleModule(pmDef.key)} />
                <div className="st-flex-1">
                  <div className="st-row-name">
                    {pmDef.display_name}
                    <span className="st-tag">PREMIUM</span>
                  </div>
                  <div className="st-row-desc">
                    {pmDef.description.substring(0, 80)}...
                  </div>
                </div>
                {isEnabled && (
                  <div className="st-num-wrap">
                    <input className="framework-builder__number-input" type="number"
                      value={config?.points_allocated || pmDef.default_points}
                      onChange={e => onModulePointsChange(pmDef.key, Number(e.target.value))}
                      min={1} max={totalPoints} />
                    <span className="st-unit">pts</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {availableIndicators.length > 0 && (
        <AddIndicatorDropdown available={availableIndicators} onAdd={onAdd} />
      )}
    </div>
  );
};

// =============================================================================
// INDICATOR ROW
// =============================================================================

const IndicatorRow: React.FC<{
  config: IndicatorConfig; definition?: IndicatorDefinition;
  onWeightChange: (weight: number) => void; onRemove: () => void;
  onThresholdsChange: (thresholds: ThresholdBucket[]) => void;
}> = ({ config, definition, onWeightChange, onRemove, onThresholdsChange }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="indicator-row">
      <div className="st-ind-top">
        <button className="fw-btn-icon" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ChevronUpIcon className="st-ico-sm" /> : <ChevronDownIcon className="st-ico-sm" />}
        </button>
        <span className="st-row-name st-flex-1">
          {definition?.display_name || config.indicator_key}
        </span>
        <div className="st-num-wrap">
          <input className="framework-builder__number-input" type="number" value={config.weight}
            onChange={e => onWeightChange(Number(e.target.value))} min={1} max={100} />
          <span className="st-unit">pts</span>
          <button className="fw-btn-icon fw-btn-icon--danger" onClick={onRemove}>
            <TrashIcon className="st-ico-sm" />
          </button>
        </div>
      </div>
      {expanded && definition && (
        <div className="st-ind-body">
          <p className="st-row-desc st-ind-desc">{definition.description}</p>
          <ThresholdEditor thresholds={config.thresholds} onChange={onThresholdsChange} />
        </div>
      )}
    </div>
  );
};

// =============================================================================
// THRESHOLD EDITOR
// =============================================================================

const ThresholdEditor: React.FC<{
  thresholds: ThresholdBucket[]; onChange: (thresholds: ThresholdBucket[]) => void;
}> = ({ thresholds, onChange }) => {
  const colors = ['var(--tk-gain)', 'var(--tk-t2)', 'var(--tk-warn)', 'var(--tk-loss)', 'var(--tk-t3)'];
  const updateBucket = (index: number, field: keyof ThresholdBucket, value: any) => {
    onChange(thresholds.map((t, i) => i !== index ? t : { ...t, [field]: value }));
  };

  return (
    <div className="st-thr">
      <div className="threshold-grid-header">
        <span>Min</span><span>Max</span><span>Score</span><span>Label</span>
      </div>
      {thresholds.map((t, i) => (
        <div key={i} className="threshold-grid-row">
          <input className="threshold-input" type="number" value={t.min ?? ''} step="any" placeholder="-inf"
            onChange={e => updateBucket(i, 'min', e.target.value === '' ? null : Number(e.target.value))} />
          <input className="threshold-input" type="number" value={t.max ?? ''} step="any" placeholder="+inf"
            onChange={e => updateBucket(i, 'max', e.target.value === '' ? null : Number(e.target.value))} />
          <div className="st-thr-score">
            <i style={{ background: colors[Math.min(i, colors.length - 1)] }} />
            <span>{Math.round(t.score_pct * 100)}%</span>
          </div>
          <input className="threshold-input" type="text" value={t.label}
            onChange={e => updateBucket(i, 'label', e.target.value)} />
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// ADD INDICATOR DROPDOWN
// =============================================================================

const AddIndicatorDropdown: React.FC<{
  available: IndicatorDefinition[]; onAdd: (def: IndicatorDefinition) => void;
}> = ({ available, onAdd }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="st-add">
      <button className="add-indicator-btn" onClick={() => setOpen(!open)}>
        <PlusIcon className="st-ico-sm" /> Add Indicator
      </button>
      {open && (
        <div className="add-indicator-dropdown">
          {available.map((def, i) => (
            <React.Fragment key={def.key}>
              {def.group && def.group !== available[i - 1]?.group && (
                <div className="add-indicator-group">{def.group}</div>
              )}
              <button className="add-indicator-option" title={def.description}
                onClick={() => { onAdd(def); setOpen(false); }}>
                <div className="st-add-name">
                  {def.display_name}
                  {def.in_default && (
                    <span className="add-indicator-default" title="Used by the default CC Score">default</span>
                  )}
                </div>
                <div className="st-row-desc">
                  {def.description.length > 70 ? `${def.description.substring(0, 70)}…` : def.description}
                </div>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default FrameworkBuilder;
